import { useCallback, useMemo } from 'react'
import {
  addSprintToRow,
  isAssociatedWithSomeSprint,
  isAssociatedWithTheSprint,
  isProductBacklogItemRow,
  ProductBacklogItemRow,
  ProductBacklogRow,
  removeSprintOfRow,
  UNSET_SPRINT_ROW,
  setSprintOfRow,
  isUnsetSprintRowUuid,
} from '../productBacklog'
import { moveRowsToLastChild } from '../../../containers/BulkSheetView/hooks/actions/moveTreeRows'
import { SprintDetail } from '../../../../lib/functions/sprint'
import { useDispatch } from 'react-redux'
import { requireSave } from '../../../../store/requiredSaveData'
import {
  insertRowsAfterSprint,
  filterBySprintConsistency,
  removeRowsFromData,
  createUnsetSprintPbiRow,
  insertCopiedRowForMultiSprint,
} from './sprintOperations'

/**
 * PBIにスプリントを設定する
 */
export type SetSprint = (
  sprintUuid: string,
  pbiRows: ProductBacklogItemRow[],
  currentSprintUuid?: string
) => void
/**
 * PBIのスプリントをドラッグ＆ドロップで付け替える
 */
export type SwitchSprint = (
  sprintUuid: string,
  pbiRows: ProductBacklogItemRow[]
) => void
export type RemoveFromParentSprintRow = (
  pbiRows: ProductBacklogItemRow[]
) => void
export type AddSprint = (
  sprintUuid: string,
  pbiRows: ProductBacklogItemRow[]
) => void

export const useSprintManager = (
  data: ProductBacklogRow[],
  setData: (data: ProductBacklogRow[]) => void,
  sprints: SprintDetail[]
): {
  setSprint: SetSprint
  switchSprint: SwitchSprint
  removeFromParentSprintRow: RemoveFromParentSprintRow
  addSprint: AddSprint
} => {
  const dispatch = useDispatch()
  const sprintMapByUuid = useMemo(() => {
    const map: { [uuid: string]: SprintDetail } = {}
    sprints.forEach(sprint => {
      map[sprint.uuid] = sprint
    })
    return map
  }, [sprints])

  const setSprint = useCallback(
    (
      sprintUuid: string,
      pbiRows: ProductBacklogItemRow[],
      currentSprintUuid?: string
    ) => {
      const targetSprint = sprintMapByUuid[sprintUuid]
      if (!targetSprint) return

      // Exclude PBIs already associated with the target sprint.
      const pbiRowUuidsToMove = new Set<string>()
      pbiRows.forEach(v => {
        if (!isAssociatedWithTheSprint(v, sprintUuid)) {
          pbiRowUuidsToMove.add(v.uuid)
        }
      })
      if (pbiRowUuidsToMove.size === 0) return

      // スプリント未設定から移動する行かどうか
      const isFromUnsetSprint =
        currentSprintUuid && isUnsetSprintRowUuid(currentSprintUuid)

      // 移動対象の行を削除し、スプリント情報を更新
      const { newData: dataWithoutRows, removedRows } = removeRowsFromData(
        data,
        pbiRowUuidsToMove,
        row => {
          // currentSprintUuidが指定されていて、UNSET_SPRINTでなく、既にスプリントに紐付いている場合
          if (
            currentSprintUuid &&
            !isFromUnsetSprint &&
            isAssociatedWithSomeSprint(row)
          ) {
            // 現在のスプリントを削除して、新しいスプリントを追加
            const currentSprint = sprintMapByUuid[currentSprintUuid]
            if (currentSprint) {
              removeSprintOfRow(row, currentSprint)
            }
            addSprintToRow(row, targetSprint)
            // treeValueも更新
            row.treeValue = [sprintUuid, row.uuid]
          } else {
            // currentSprintUuidが未指定またはUNSET_SPRINTの場合は、すべてのスプリントを上書き
            setSprintOfRow(row, targetSprint)
          }
          return row
        }
      )

      // 移動先スプリントの最後に挿入
      let newData = insertRowsAfterSprint(
        dataWithoutRows,
        sprintUuid,
        removedRows
      )

      // 親スプリントとの整合性でフィルタリング
      newData = filterBySprintConsistency(newData)

      setData(newData)
      dispatch(requireSave())
    },
    [sprintMapByUuid, data, setData, dispatch]
  )

  const switchSprint = useCallback(
    (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) => {
      // Exclude PBIs already associated with the target sprint.
      const pbiRowsToMove: ProductBacklogItemRow[] = []
      const fromSprintRowUuidMapByPbiRow: { [uuid: string]: string } = {}
      pbiRows.forEach(v => {
        if (!isAssociatedWithTheSprint(v, sprintUuid)) {
          pbiRowsToMove.push(v)
          fromSprintRowUuidMapByPbiRow[v.uuid] = v.treeValue[0]
        }
      })
      if (pbiRowsToMove.length === 0) return

      let newData = moveRowsToLastChild(data, pbiRowsToMove, sprintUuid)
      pbiRowsToMove.forEach(row => {
        const fromSprintRowUuid = fromSprintRowUuidMapByPbiRow[row.uuid]
        if (!isUnsetSprintRowUuid(fromSprintRowUuid)) {
          // 移動元が未設定スプリントではない場合は、移動元のスプリントを削除する
          removeSprintOfRow(row, sprintMapByUuid[fromSprintRowUuid])
        }
        if (isUnsetSprintRowUuid(sprintUuid)) {
          // 移動先が未設定スプリントの場合、スプリントの紐付きを解除する
          setSprintOfRow(row, undefined)
        } else {
          // 移動先が未設定スプリントではない場合、移動先のスプリントを新たに紐づける
          addSprintToRow(row, sprintMapByUuid[sprintUuid])
        }
      })

      // 親スプリントとの整合性でフィルタリング
      newData = filterBySprintConsistency(newData)

      setData(newData)
      dispatch(requireSave())
    },
    [sprintMapByUuid, data, setData, dispatch]
  )

  const removeFromParentSprintRow = useCallback(
    (pbiRows: ProductBacklogItemRow[]) => {
      const pbiRowUuids: string[] = []
      const editedPbiRowMapByOriginalUuid: {
        [uuid: string]: ProductBacklogItemRow
      } = {}
      const pbiRowsNotAssociatedWithSprint: ProductBacklogItemRow[] = []

      pbiRows.forEach(pbiRow => {
        const parentSprintRowUuid = pbiRow.treeValue[0]
        const parentSprint = sprintMapByUuid[parentSprintRowUuid]
        if (!parentSprint) return
        pbiRowUuids.push(pbiRow.uuid)

        removeSprintOfRow(pbiRow, parentSprint)
        editedPbiRowMapByOriginalUuid[pbiRow.originalUuid] = pbiRow

        if (!pbiRow.wbsItem.sprints || pbiRow.wbsItem.sprints.length === 0) {
          pbiRowsNotAssociatedWithSprint.push(createUnsetSprintPbiRow(pbiRow))
        }
      })

      const newData = data.filter(v => {
        if (
          isProductBacklogItemRow(v) &&
          editedPbiRowMapByOriginalUuid[v.originalUuid]
        ) {
          const editedPbiRow = editedPbiRowMapByOriginalUuid[v.originalUuid]
          v.edited = editedPbiRow.edited
          v.editedData = editedPbiRow.editedData
        }
        return !pbiRowUuids.includes(v.uuid)
      })
      newData.push(...pbiRowsNotAssociatedWithSprint)

      setData(newData)
      dispatch(requireSave())
    },
    [data, setData, dispatch, sprintMapByUuid]
  )

  const addSprint = useCallback(
    (sprintUuid: string, pbiRows: ProductBacklogItemRow[]) => {
      const targetSprint = sprintMapByUuid[sprintUuid]
      if (!targetSprint) return

      const targetPbiRowsBeforeSetSprint: ProductBacklogItemRow[] = []
      const targetPbiRowsBeforeUnsetSprint: ProductBacklogItemRow[] = []

      // Exclude PBIs already associated with the target sprint.
      pbiRows.forEach(v => {
        if (isAssociatedWithTheSprint(v, sprintUuid)) {
          return
        }
        if (isAssociatedWithSomeSprint(v)) {
          targetPbiRowsBeforeSetSprint.push(v)
        } else {
          targetPbiRowsBeforeUnsetSprint.push(v)
        }
        addSprintToRow(v, targetSprint)
      })

      let newData =
        targetPbiRowsBeforeUnsetSprint.length > 0
          ? moveRowsToLastChild(
              data,
              targetPbiRowsBeforeUnsetSprint,
              sprintUuid
            )
          : [...data]

      // 既にスプリントに紐付いている行のコピーを追加
      for (const row of targetPbiRowsBeforeSetSprint) {
        const result = insertCopiedRowForMultiSprint(newData, sprintUuid, row)
        newData = result.newData
      }

      setData(newData)
      dispatch(requireSave())
    },
    [sprintMapByUuid, data, setData, dispatch]
  )

  return {
    setSprint,
    switchSprint,
    removeFromParentSprintRow,
    addSprint,
  }
}
